import styled from 'styled-components';
import * as C from '../../../../../ui/common/CommonStyles';
import { operationApis } from '../../../../../../shared/apis/operation/apis';
import { AxiosError } from 'axios';
import { handleError } from '../../../../../ui/err/handleError';
import ToastEditor from './Editor';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../../../../../../shared/queryClient';
import { AxiosCommonError } from '../../../../../../shared/types';
import * as M from '@mantine/core';
import Swal from 'sweetalert2';
import { languageOptions, noticeTypeOptions, solutionOptions } from '../../../../../../shared/constants/commonCode';
import { IconCheck, IconX } from '@tabler/icons-react';

export default function NoticeReg() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [, setImages] = useState<File[]>([]); // 이미지 업로드를 위한 상태값
  const id = searchParams.get('id') || '';
  const editorRef = useRef<any>(null);

  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: {
      solutionCd: solutionOptions[0].value,
      languageCd: 'LT100002',
      noticeCd: 'NT100001',
      title: '',
      isPinned: false,
    },
  });

  // 수정 시 공지사항 상세 조회
  const { data: detail } = useQuery(['getAnnouncementDetail', id], () => operationApis.getNoticeDetail(id), {
    enabled: !!id,
    select: (data) => data.data.result[0],
    onError: (error: AxiosError<AxiosCommonError>) => {
      handleError(error);
    },
  });

  useEffect(() => {
    if (detail) {
      // 상세 조회 데이터를 폼에 삽입
      reset({
        solutionCd: detail.solutionCd,
        languageCd: detail.languageCd,
        noticeCd: detail.noticeCd,
        title: detail.title,
        isPinned: detail.isPinned,
      });

      // 에디터가 존재하는지 확인 후 HTML 설정
      const editorInstance = editorRef.current.getInstance?.(); // ? 연산자 추가
      if (editorInstance && typeof editorInstance.setHTML === 'function') {
        editorInstance.setHTML(detail.content);
      } else if (editorRef.current.setHTML) {
        editorRef.current.setHTML(detail.content);
      }
    }
  }, [detail]);

  // 공지사항 생성
  const { mutate: add } = useMutation(operationApis.createNotice, {
    onSuccess: (data) => {
      Swal.fire({
        icon: 'success',
        title: '공지사항이 등록되었습니다.',
        showConfirmButton: true,
      }).then(() => {
        navigate(`/admin/edgecross/notice/${data.data.id}`);
        reset();
        setImages([]);
      });
    },
    onError: (error: AxiosError<AxiosCommonError>) => {
      handleError(error);
    },
  });

  // 공지사항 수정
  const { mutate: edit } = useMutation(operationApis.updateNotice, {
    onSuccess: async () => {
      Swal.fire({
        icon: 'success',
        title: '공지사항이 수정되었습니다.',
        showConfirmButton: true,
      });
      await queryClient.refetchQueries(['getNoticeDetail', id]);
      navigate(`/admin/edgecross/notice/${id}`);
      reset();
      setImages([]);
    },
    onError: (error: AxiosError<AxiosCommonError>) => {
      handleError(error);
    },
  });

  // 공지사항 제출
  const onSubmit = (data: any) => {
    const announcement = {
      ...data,
      content: editorRef.current?.getInstance().getHTML(),
    };

    if (id) {
      edit({ ...announcement, id });
    } else {
      add(announcement);
    }
  };

  const onClickCancel = () => {
    Swal.fire({
      title: '작성을 취소하시겠습니까?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: '확인',
      cancelButtonText: '취소',
    }).then((result) => {
      if (result.isConfirmed) {
        reset();
        setImages([]);
        navigate('/admin/edgecross/notice');
      }
    });
  };

  return (
    <div>
      <C.TitleBox>
        <div className="title">공지사항 작성</div>
      </C.TitleBox>
      <form onSubmit={handleSubmit(onSubmit)}>
        <C.ListBox>
          <Wrapper>
            <div style={{ display: 'flex' }}>
              <Box>
                <M.Text miw="10rem" fw="bold" size="lg">
                  솔루션
                </M.Text>
                <Controller
                  name="solutionCd"
                  rules={{
                    required: '솔루션을 선택해주세요.',
                  }}
                  control={control}
                  render={({ field }) => <M.Select {...field} w="100%" data={solutionOptions} />}
                />
              </Box>
              <Box>
                <M.Text miw="10rem" fw="bold" size="lg">
                  언어
                </M.Text>
                <Controller
                  name="languageCd"
                  rules={{
                    required: '언어를 선택해주세요.',
                  }}
                  control={control}
                  render={({ field }) => <M.Select {...field} w="100%" data={languageOptions} />}
                />
              </Box>
            </div>
            <div style={{ display: 'flex' }}>
              <Box>
                <M.Text miw="10rem" fw="bold" size="lg">
                  공지 유형
                </M.Text>
                <Controller
                  name="noticeCd"
                  rules={{
                    required: '공지 유형를 선택해주세요.',
                  }}
                  control={control}
                  render={({ field }) => <M.Select {...field} w="100%" data={noticeTypeOptions} />}
                />
              </Box>
              <Box>
                <M.Text miw="10rem" fw="bold" size="lg">
                  대표글 설정
                </M.Text>
                <Controller
                  name="isPinned"
                  control={control}
                  render={({ field }) => (
                    <M.Switch
                      size="lg"
                      checked={field.value}
                      onChange={(value) => field.onChange(value)}
                      thumbIcon={
                        field.value ? (
                          <IconCheck size={14} color="var(--mantine-color-teal-6)" stroke={3} />
                        ) : (
                          <IconX size={14} color="var(--mantine-color-red-6)" stroke={3} />
                        )
                      }
                    />
                  )}
                />
              </Box>
            </div>
            <Box>
              <M.Text miw="10rem" fw="bold" size="lg">
                제목
              </M.Text>
              <M.TextInput
                w="100%"
                {...register('title', {
                  required: '제목을 입력해주세요.',
                })}
                error={errors.title?.message}
              />
            </Box>
          </Wrapper>
          <ToastEditor editorRef={editorRef} setImages={setImages} />
          <M.Flex justify="center" gap="md" mt="xl">
            <M.Button type="button" variant="default" onClick={onClickCancel}>
              작성 취소
            </M.Button>
            <M.Button variant="filled" type="submit">
              작성 완료
            </M.Button>
          </M.Flex>
        </C.ListBox>
      </form>
    </div>
  );
}

const Wrapper = styled.div`
  padding: 1rem 0px;
`;

const Box = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 1rem;
  input,
  select {
    width: 100%;
    padding: 1rem;
    border: ${(props) => props.theme.border.gray1};
  }
`;
